import React from "react";
import { InlineMath } from "react-katex";
import {
  createNumericInputTask,
  DigitRow,
  StepStrip,
  WrittenBoard,
  padDigits,
  randInt,
} from "../sharedWrittenNatural";

function pow10(exp) {
  return 10 ** exp;
}

function randNDigit(digits) {
  return randInt(pow10(digits - 1), pow10(digits) - 1);
}

function needsBorrow(a, b) {
  const cols = Math.max(String(a).length, String(b).length);
  const aDigits = padDigits(a, cols).map((x) => (x === " " ? 0 : Number(x)));
  const bDigits = padDigits(b, cols).map((x) => (x === " " ? 0 : Number(x)));
  const current = [...aDigits];

  function borrowFor(col) {
    let donor = col - 1;
    while (donor >= 0 && current[donor] === 0) donor -= 1;
    if (donor < 0) return false;
    current[donor] -= 1;
    for (let k = donor + 1; k < col; k += 1) current[k] = 9;
    current[col] += 10;
    return true;
  }

  for (let c = cols - 1; c >= 0; c -= 1) {
    if (current[c] < bDigits[c]) {
      if (!borrowFor(c)) return false;
      return true;
    }
  }
  return false;
}

function pickByDigits(aDigits, bDigits) {
  for (let i = 0; i < 900; i += 1) {
    let a = randNDigit(aDigits);
    let b = randNDigit(bDigits);
    if (b >= a) [a, b] = [b, a];
    if (String(a).length === aDigits && String(b).length === bDigits && needsBorrow(a, b)) {
      return { a, b, result: a - b };
    }
  }
  const a = randNDigit(aDigits);
  const b = Math.min(randNDigit(bDigits), a - 1);
  return { a, b, result: a - b };
}

function makePicker(aDigits, bDigits) {
  return () => pickByDigits(aDigits, bDigits);
}

export function pickSubtractionLargeMixed() {
  const options = [
    [5, 3],
    [5, 4],
    [5, 5],
    [6, 4],
  ];
  const [aDigits, bDigits] = options[randInt(0, options.length - 1)];
  return pickByDigits(aDigits, bDigits);
}

export function analyzeSubtraction(a, b) {
  const result = a - b;
  const cols = Math.max(String(a).length, String(b).length, String(result).length);
  const aDisplayDigits = padDigits(a, cols);
  const bDisplayDigits = padDigits(b, cols);
  const aDigits = aDisplayDigits.map((x) => (x === " " ? 0 : Number(x)));
  const bDigits = bDisplayDigits.map((x) => (x === " " ? 0 : Number(x)));
  const current = [...aDigits];
  const resultDigits = Array(cols).fill(" ");
  const detailLines = [];
  const steps = [];

  const baseStruck = new Set();
  const active = Array(cols).fill("");
  const replaced = Array(cols).fill("");

  function applyModification(idx, nextValue, state) {
    if (active[idx] !== "") {
      replaced[idx] = String(active[idx]);
      state.replacedCurrent.push(idx);
    } else {
      baseStruck.add(idx);
      state.baseCurrent.push(idx);
    }
    active[idx] = String(nextValue);
    state.changedIndices.push(idx);
  }

  function borrowFor(col, state) {
    let donor = col - 1;
    while (donor >= 0 && current[donor] === 0) donor -= 1;
    if (donor < 0) return;

    current[donor] -= 1;
    applyModification(donor, current[donor], state);

    for (let k = donor + 1; k < col; k += 1) {
      current[k] = 9;
      applyModification(k, current[k], state);
    }

    current[col] += 10;
    applyModification(col, current[col], state);
  }

  for (let c = cols - 1; c >= 0; c -= 1) {
    const before = current[c];
    const sub = bDigits[c];
    const state = {
      changedIndices: [],
      replacedCurrent: [],
      baseCurrent: [],
    };

    if (before < sub) borrowFor(c, state);

    const minuend = current[c];
    const diff = minuend - sub;
    resultDigits[c] = String(diff);
    detailLines.push({ before, sub, borrowed: before < sub, minuend, diff });

    steps.push({
      strikeDigits: replaced.map((value) => (value ? String(value) : "")),
      newDigits: active.map((value) => (value ? String(value) : "")),
      changedIndices: state.changedIndices,
      replacedCurrent: state.replacedCurrent,
      baseStruck: Array.from(baseStruck),
      baseCurrent: state.baseCurrent,
      aDigits: [...aDisplayDigits],
      bDigits: [...bDisplayDigits],
      resultDigits: [...resultDigits],
      highlightResult: [c],
    });
  }

  steps.push({
    strikeDigits: replaced.map((value) => (value ? String(value) : "")),
    newDigits: active.map((value) => (value ? String(value) : "")),
    changedIndices: [],
    replacedCurrent: [],
    baseStruck: Array.from(baseStruck),
    baseCurrent: [],
    aDigits: [...aDisplayDigits],
    bDigits: [...bDisplayDigits],
    resultDigits: [...resultDigits],
    highlightResult: [],
  });

  return { steps, detailLines };
}

export function WrittenSubtractionBoard({ step }) {
  const hasReplaced = step.strikeDigits.map((value) => value !== "");
  const lowerDigits = step.strikeDigits.map((value, idx) =>
    hasReplaced[idx] ? value : step.newDigits[idx]
  );
  const upperDigits = step.newDigits.map((value, idx) => (hasReplaced[idx] ? value : ""));
  const lowerHighlight = step.changedIndices.filter((idx) => !hasReplaced[idx]);
  const upperHighlight = step.changedIndices.filter((idx) => hasReplaced[idx]);
  const lowerStrike = hasReplaced.map((ok, idx) => (ok ? idx : -1)).filter((idx) => idx >= 0);
  const hasUpperRow = upperDigits.some((value) => value !== "");

  return (
    <WrittenBoard>
      {hasUpperRow ? (
        <DigitRow digits={upperDigits} small highlightIndices={upperHighlight} />
      ) : null}
      <DigitRow
        digits={lowerDigits}
        small
        highlightIndices={lowerHighlight}
        strikeIndices={lowerStrike}
        strikeCurrentIndices={step.replacedCurrent}
      />
      <DigitRow
        digits={step.aDigits}
        strikeIndices={step.baseStruck}
        strikeCurrentIndices={step.baseCurrent}
      />
      <DigitRow sign="-" digits={step.bDigits} />
      <div style={{ margin: "2px 0", borderTop: "1px solid var(--color-text)" }} />
      <DigitRow digits={step.resultDigits} highlightIndices={step.highlightResult} />
    </WrittenBoard>
  );
}

function makeSubtractionTask(type, pickData) {
  return createNumericInputTask({
    type,
    pickData,
    renderTaskText: (data) => (
      <>
        Oblicz sposobem pisemnym: <InlineMath math={`${data.a}-${data.b}`} />.
      </>
    ),
    explain: (data) => {
      const written = analyzeSubtraction(data.a, data.b);
      return (
        <div style={{ textAlign: "left" }}>
          <p>
            <strong>Krok 1:</strong> Ustawiamy liczby pionowo: jedności pod
            jednościami, dziesiątki pod dziesiątkami itd.
          </p>
          <p>
            <strong>Krok 2:</strong> Odejmujemy od prawej strony. Gdy w kolumnie
            nie da się odjąć, pożyczamy 1 z lewej kolumny. Wtedy bieżąca kolumna
            dostaje 10, a pożyczona cyfra u góry jest skreślona i zmniejszona.
          </p>
          <SubtractionDetails lines={written.detailLines} />
          <StepStrip
            steps={written.steps.map((step, idx) => (
              <WrittenSubtractionBoard key={idx} step={step} />
            ))}
          />
          <p>
            <strong>Wynik:</strong> <InlineMath math={`${data.result}`} />
          </p>
        </div>
      );
    },
  });
}

export function SubtractionDetails({ lines }) {
  return (
    <ul>
      {lines.map((line, idx) => (
        <li key={idx}>
          {line.borrowed ? (
            <>
              <InlineMath math={`${line.before}<${line.sub}`} />, więc pożyczamy i liczymy{" "}
              <InlineMath math={`${line.minuend}-${line.sub}=${line.diff}`} />
            </>
          ) : (
            <InlineMath math={`${line.before}-${line.sub}=${line.diff}`} />
          )}
        </li>
      ))}
    </ul>
  );
}

export const NaturalWrittenSubtractionTwoByTwoTask = makeSubtractionTask(
  "NaturalWrittenSubtractionTwoByTwoTask",
  makePicker(2, 2)
);
export const NaturalWrittenSubtractionThreeByTwoTask = makeSubtractionTask(
  "NaturalWrittenSubtractionThreeByTwoTask",
  makePicker(3, 2)
);
export const NaturalWrittenSubtractionThreeByThreeTask = makeSubtractionTask(
  "NaturalWrittenSubtractionThreeByThreeTask",
  makePicker(3, 3)
);
export const NaturalWrittenSubtractionFourByFourTask = makeSubtractionTask(
  "NaturalWrittenSubtractionFourByFourTask",
  makePicker(4, 4)
);
export const NaturalWrittenSubtractionLargeMixedTask = makeSubtractionTask(
  "NaturalWrittenSubtractionLargeMixedTask",
  pickSubtractionLargeMixed
);
